<template>
  <v-app>
    <template v-if="isIE">
      <v-dialog v-model="ieModal" max-width="384" lazy persistent content-class="model-dialog">
        <ie-modal />
      </v-dialog>
    </template>
    <template v-else-if="regionNotSelected">
      <v-dialog v-model="chooseRegion" max-width="384" lazy persistent content-class="model-dialog">
        <choose-region-modal />
      </v-dialog>
    </template>
    <template v-else-if="loadApp">
      <v-dialog v-model="showTosModal" v-if="notCorrectTosAccepted" max-width="600" lazy persistent content-class="model-dialog">
        <terms-of-use-modal />
      </v-dialog>
      <the-wrong-screen-size v-if="showWrongSizeScreen" />
      <router-view v-else />
    </template>
    <the-spinner v-else></the-spinner>
  </v-app>
</template>

<script>
import { userManager, AuthControl } from '@/services/SecurityService';
import store from '@/store';
import { createNamespacedHelpers } from 'vuex';

const { mapGetters, mapActions } = createNamespacedHelpers('commonState');
import { getterTypes, actionTypes } from '@/views/common/store/common.moduletypes';
import i18n from '@/plugins/i18n';

import TheSpinner from '@/components/TheSpinner';
import ChooseRegionModal from '@/components/modals/ChooseRegionModal';
import TermsOfUseModal from '@/components/modals/TermsOfUseModal';
import IeModal from '@/components/modals/IeModal';
import TheWrongScreenSize from '@/components/TheWrongScreenSize';

export default {
  components: {
    TheSpinner,
    ChooseRegionModal,
    TermsOfUseModal,
    IeModal,
    TheWrongScreenSize
  },
  data() {
    return {
      authControl: new AuthControl(),
      termsAccepted: true,
      loadApp: false,
      userLoggedIn: false,
      chooseRegion: true,
      showTosModal: true,
      screenWidth: 0,
      ieModal: true,
      isIE: false
    };
  },
  computed: {
    ...mapGetters([getterTypes.getAcceptedTos, getterTypes.getLastTosVersion, getterTypes.getProfile]),
    regionNotSelected() {
      return this.$store.getters.getRegionNotSelected;
    },
    notCorrectTosAccepted() {
      // eslint-disable-next-line
      return this.userLoggedIn && (this[getterTypes.getAcceptedTos] < this[getterTypes.getLastTosVersion]);
    },
    showWrongSizeScreen() {
      return this.screenWidth < 768 && this.$router.currentRoute.name !== 'landing';
    }
  },
  methods: {
    ...mapActions([actionTypes.getProfile, actionTypes.getRegions]),
    closeTermsPage() {
      this.termsAccepted = true;
    },
    checkUserStatus() {
      return new Promise(resolve => {
        // if we have token in url sign it
        if (window.location.hash.substring(1) !== '' && window.location.hash.substring(1).includes('id_token')) {
          this.authControl.signInRedirectCallback(window.location.href);
        } else {
          // check if external library can log in with current token
          this.authControl
            .getSignedIn()
            // if everything ok, flag user in our system as logged in
            .then(res => {
              if (res) {
                this.userLoggedIn = true;
                store.dispatch('logInUser');
              } else {
                // if redirect comes from myidentity, use different way to login user
                if (window.location.hash.substring(1).includes('myidentitylogin')) {
                  userManager.signinRedirect();
                } else {
                  // if we cannot log in user, mark it as log out
                  this.userLoggedIn = false;
                  store.dispatch('logOutUser');
                }
              }
              resolve();
            })
            .catch(error => console.log(error));
        }
      });
    },
    handleResize() {
      this.screenWidth = window.innerWidth;
    }
  },
  mounted() {
    // Method to remove stale records from localStorage
    userManager.clearStaleState();
  },
  created() {
    let ua = window.navigator.userAgent;
    let msie = ua.indexOf('MSIE ');

    // https://stackoverflow.com/questions/56360225/how-to-detect-microsoft-chromium-edge-chredge-edgium-in-javascript
    if (msie > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./) || ua.indexOf('edge') > -1) {
      this.isIE = true;
    }
    this.checkUserStatus().then(() => {
      let promises = [];
      if (this.userLoggedIn) {
        promises.push(this[actionTypes.getProfile]());
      }
      promises.push(this[actionTypes.getRegions]());
      Promise.all(promises).then(() => {
        this.loadApp = true;

        // set up app language if it is changes somewhere
        if (this.userLoggedIn) {
          let profile = this[getterTypes.getProfile];
          if (localStorage.getItem('currentLanguage') !== profile.currentLanguage) {
            localStorage.setItem('currentLanguage', profile.currentLanguage);
            i18n.locale = profile.currentLanguage;
          }
        }
      });
      window.addEventListener('resize', this.handleResize);
      this.handleResize();
    });
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    regionNotSelected: function(bool) {
      if (!bool) {
        this.$router.go();
      }
    }
  }
};
</script>
<style lang="scss">
$material-design-icons-font-path: '~material-design-icons-iconfont/dist/fonts/';
@import '~material-design-icons-iconfont/src/material-design-icons';
@import url('https://fonts.googleapis.com/css?family=Roboto:300,400,500,700');

#app {
  font-family: 'Roboto', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  background-color: #ffffff;
  width: 100%;
  overflow-x: hidden;
}

.card-label {
  font-weight: bold;
  display: block;
  text-align: left;
  text-transform: capitalize;
  color: var(--v-blueGrey-base);
  font-size: 12px;
}

.model-dialog.v-dialog {
  box-shadow: none;
}

.reset-input {
  .v-input__slot {
    border: 1px solid var(--v-lightBlueGrey-base) !important;
    border-radius: 4px !important;
    height: 40px !important;
    min-height: 40px !important;
    margin-top: 10px;

    input {
      margin-top: 0;
      max-height: none;
    }

    .v-select__selections {
      padding-top: 0 !important;
    }

    .v-input__append-inner {
      margin-top: 8px !important;
    }
  }
}

.reset-to-blue-input {
  position: relative;

  .v-input__slot {
    width: 100%;
    max-width: 224px;
    border: 1px solid var(--v-lightBlue-base) !important;

    .theme--light.v-icon {
      color: var(--v-lightBlue-base) !important;
    }

    .theme--light.v-label {
      color: var(--v-lightBlue-base) !important;
    }

    .v-text-field__slot input {
      color: var(--v-lightBlue-base) !important;
    }
  }

  .v-input__control {
    flex-direction: row-reverse;
  }
}

.reset-to-blue-select {
  .v-select__selections,
  .v-icon {
    color: var(--v-lightBlue-base) !important;
  }

  .v-text-field.v-text-field--enclosed .v-input__append-inner {
    margin-top: 10px;
  }

  .v-input__control .v-input__slot {
    border: 1px solid var(--v-lightBlue-base) !important;
    height: 44px !important;
    min-height: 44px !important;
    width: 192px;
    font-size: 14px;

    input {
      margin-top: 0;
      max-height: none;
    }

    .v-select__selections {
      padding-top: 0 !important;
    }

    input {
      color: var(--v-lightBlue-base);

      textarea::placeholder {
        color: var(--v-lightBlue-base) !important;
      }
    }
  }

  .theme--light.v-input:not(.v-input--is-disabled) input {
    color: var(--v-lightBlue-base) !important;
  }
}

.pagination-redesign {
  .v-pagination {
    &__navigation {
      .v-icon {
        color: #ffffff !important;
        background-color: var(--v-truetzschlerNavy-base);
        border-radius: 4px;
      }

      &--disabled {
        background-color: var(--v-truetzschlerNavy-base);
        opacity: 0.1;
      }
    }

    &__item {
      background-color: transparent !important;
      box-shadow: none;
      color: #c9c9c9 !important;
      min-width: 20px !important;
      outline: 0 !important;

      &--active {
        color: var(--v-truetzschlerNavy-base) !important;
        font-weight: bold;
      }
    }
  }
}

.orange-progress {
  background-color: #ff8400 !important;
}
.blue-progress {
  background-color: #00468c !important;
}
.red-progress {
  background-color: #ff0000 !important;
}
.h-100{
  height: 100%;
}
</style>
